import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Check,
  PencilLine,
  RotateCcw,
  BoxSelect,
  Trash2,
  X,
} from "lucide-react";
import clsx from "clsx";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import type { AnalyticsEventProperties } from "utils/analytics";
import { PanelSelect } from "../../../PanelSelect";
import { systemMeta } from "../../cableSystems";
import type { SourceRowProps } from "./types";
import { BoardFeedsEntry } from "../../BoardFeedsEntry";
import { CATEGORY_META } from "../../deviceCategories";

export const SourceRow = ({
  entry,
  areaCount = 0,
  onFeedArea,
  onRemoveAreas,
  moveTargets,
  formatMetres,
  onRename,
  onRemoveTo,
  onRemove,
  onRestore,
  feeds,
}: SourceRowProps) => {
  const { t } = useTranslation();

  const [draftName, setDraftName] = useState<string | null>(null);
  const [isStriking, setIsStriking] = useState(false);
  const [isEditingFeeds, setIsEditingFeeds] = useState(false);

  const meta = systemMeta(entry.system);
  const carriesNothing = entry.circuitCount === 0;

  const trackFeedPointChanged = (
    action: AnalyticsEventProperties<"Smart Surveyor Cable Feed Point Changed">["action"],
  ) =>
    trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_CABLE_FEED_POINT_CHANGED, {
      action,
    });

  const commitName = () => {
    const next = draftName?.trim();
    setDraftName(null);
    if (!next || next === entry.name) return;
    trackFeedPointChanged("renamed");
    onRename(entry.label, next);
  };

  const strike = (toSourceLabel: string) => {
    setIsStriking(false);
    trackFeedPointChanged("removed");
    onRemoveTo(entry.label, toSourceLabel);
  };

  return (
    <li
      data-testid={`source-${entry.label}`}
      className={clsx(
        "rounded-md border px-2 py-1.5",
        entry.isRemoved
          ? "border-slate-200 bg-slate-50"
          : "border-slate-200 bg-white",
      )}
    >
      <div className="flex items-center gap-2">
        {/* The marker as the plan draws it: a board is amber and carries its
            own label, so the list and the drawing name it the same way. */}
        <span
          className={clsx(
            "grid h-9 w-9 flex-none place-items-center rounded-md border text-[13px] font-bold",
            entry.isRemoved
              ? "border-slate-300 bg-slate-100 text-slate-400 line-through"
              : "border-amber-300 bg-amber-100 text-amber-900",
          )}
          title={entry.label}
        >
          {entry.label.slice(0, 3)}
        </span>

        <span className="flex min-w-0 flex-1 flex-col">
          {draftName === null ? (
            <span
              className={clsx(
                "truncate text-[14px] font-medium",
                entry.isRemoved
                  ? "text-slate-500 line-through"
                  : "text-slate-800",
              )}
              title={entry.name}
            >
              {entry.name}
            </span>
          ) : (
            <input
              autoFocus
              value={draftName}
              aria-label={t("smartSurveyor.cablesBeta.sourceRename")}
              onChange={(event) => setDraftName(event.target.value)}
              onBlur={commitName}
              onKeyDown={(event) => {
                if (event.key === "Enter") commitName();
                if (event.key === "Escape") setDraftName(null);
              }}
              className="h-8 w-full rounded border border-blue-400 px-1.5 text-[14px] text-slate-800 focus:outline-none"
            />
          )}
          <span className="flex items-center gap-1.5 text-[13px] text-slate-500">
            {!entry.isRemoved && (
              <span
                className={clsx("h-2 w-2 flex-none rounded-full", meta.dot)}
              />
            )}
            <span className="truncate tabular-nums">
              {/* `count`, not `circuits`: the suffix plurals are keyed on it,
                  and "1 circuits" is a bug he has already met once. */}
              {t("smartSurveyor.cablesBeta.sourceCarries", {
                count: entry.circuitCount,
                metres: formatMetres(entry.metres),
              })}
            </span>
          </span>
          {/* Stated where the board is, because a rule he cannot see is a rule
              that changes his metres for no reason he can point at. */}
          {areaCount > 0 && (
            <span className="flex items-center gap-1.5 text-[13px] text-blue-700">
              <BoxSelect size={13} aria-hidden className="flex-none" />
              <span className="truncate">
                {t("smartSurveyor.cablesBeta.feedAreaListTitle", {
                  count: areaCount,
                })}
              </span>
              {onRemoveAreas && (
                <button
                  type="button"
                  onClick={() => {
                    trackFeedPointChanged("zonesRemoved");
                    onRemoveAreas(entry.label);
                  }}
                  title={t("smartSurveyor.cablesBeta.feedAreaRemove")}
                  className="flex-none rounded px-1 font-medium text-slate-500 hover:text-red-600 hover:underline"
                >
                  <X size={13} />
                </button>
              )}
            </span>
          )}
        </span>

        {entry.isRemoved ? (
          <button
            type="button"
            onClick={() => {
              trackFeedPointChanged("restored");
              onRestore(entry.label);
            }}
            className="flex h-9 flex-none items-center gap-1.5 rounded-md border border-blue-200 bg-white px-2.5 text-[13px] font-medium text-blue-700 hover:border-blue-400 hover:bg-blue-50"
          >
            <RotateCcw size={15} />
            {t("smartSurveyor.cablesBeta.sourceRestore")}
          </button>
        ) : (
          <span className="flex flex-none items-center gap-1">
            {/* A region, not a run: this is the only control here that writes a
                RULE, so it says which board it is arming and the areas it has
                already are counted on the row that owns them. */}
            {onFeedArea && (
              <button
                type="button"
                onClick={() => {
                  trackFeedPointChanged("zoneDrawn");
                  onFeedArea(entry.label);
                }}
                title={t("smartSurveyor.cablesBeta.sourceFeedArea")}
                aria-label={t("smartSurveyor.cablesBeta.sourceFeedArea")}
                className="grid h-9 w-9 place-items-center rounded-md border border-transparent text-slate-500 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
              >
                <BoxSelect size={16} />
              </button>
            )}
            <button
              type="button"
              onClick={() => setDraftName(entry.name)}
              title={t("smartSurveyor.cablesBeta.sourceRename")}
              className="grid h-9 w-9 place-items-center rounded-md border border-transparent text-slate-500 hover:border-slate-300 hover:bg-white hover:text-blue-600"
            >
              <PencilLine size={16} />
            </button>
            <button
              type="button"
              onClick={() => {
                if (carriesNothing) {
                  trackFeedPointChanged("removed");
                  onRemove(entry.label);
                  return;
                }
                setIsStriking((open) => !open);
              }}
              aria-expanded={isStriking}
              title={t("smartSurveyor.cablesBeta.sourceRemove")}
              className={clsx(
                "grid h-9 w-9 place-items-center rounded-md border",
                isStriking
                  ? "border-red-300 bg-red-50 text-red-600"
                  : "border-transparent text-slate-500 hover:border-red-200 hover:bg-red-50 hover:text-red-600",
              )}
            >
              <Trash2 size={16} />
            </button>
          </span>
        )}
      </div>

      {feeds && !entry.isRemoved && (
        <div className="mt-1 flex items-center gap-1.5 text-[13px] text-slate-600">
          <span className="min-w-0 flex-1 truncate">
            {feeds.resolved.feed.categories.length
              ? t("smartSurveyor.cablesBeta.boardFeedsLine", {
                  categories: feeds.resolved.feed.categories
                    .map((category) => t(CATEGORY_META[category].i18nKey))
                    .join(", "),
                })
              : t("smartSurveyor.cablesBeta.boardFeedsNone")}
            {feeds.resolved.from === "suggested" && (
              <span className="ml-1 text-slate-400">
                ({t("smartSurveyor.cablesBeta.boardFeedsSuggested")})
              </span>
            )}
          </span>
          <button
            type="button"
            onClick={() => setIsEditingFeeds((open) => !open)}
            aria-expanded={isEditingFeeds}
            className="flex-none rounded px-1 font-medium text-blue-700 hover:underline"
          >
            {t("smartSurveyor.cablesBeta.boardFeedsEdit")}
          </button>
        </div>
      )}
      {feeds && isEditingFeeds && !entry.isRemoved && (
        <BoardFeedsEntry
          feed={feeds.resolved.feed}
          typesByCategory={feeds.typesByCategory}
          onSave={(feed) => {
            feeds.onSave(feed);
            setIsEditingFeeds(false);
          }}
          onCancel={() => setIsEditingFeeds(false)}
        />
      )}

      {entry.isUncertain && !entry.isRemoved && (
        <p className="mt-1 text-[13px] text-amber-700">
          {t("smartSurveyor.cablesBeta.sourceGuessedPosition")}
        </p>
      )}

      {/* So a board of his own reading "0 circuits" reads as deliberate rather
          than as the defect the chip above is counting. */}
      {entry.isAdded && !entry.isRemoved && (
        <p className="mt-1 text-[13px] text-blue-700">
          {t("smartSurveyor.cablesBeta.sourcePlacedByYou")}
        </p>
      )}

      {/* The consequence BEFORE he commits, and one question settles it. A board
          struck without this left six circuits fed from nowhere at 0 m, which is
          the state he could not get out of. */}
      {isStriking && !entry.isRemoved && (
        <div className="mt-1.5 rounded-md border border-red-200 bg-red-50 p-2">
          <p className="text-[13px] text-slate-800">
            {t("smartSurveyor.cablesBeta.sourceRemoveConsequence", {
              count: entry.circuitCount,
              source: entry.name,
            })}
          </p>
          {moveTargets.length > 0 ? (
            <div className="mt-1.5">
              <PanelSelect
                value=""
                options={moveTargets}
                placeholder={t("smartSurveyor.cablesBeta.sourceMoveThemTo")}
                ariaLabel={t("smartSurveyor.cablesBeta.sourceMoveThemTo")}
                onPick={strike}
              />
            </div>
          ) : (
            <p className="mt-1.5 text-[13px] font-medium text-red-700">
              {t("smartSurveyor.cablesBeta.sourceNowhereToMove")}
            </p>
          )}
          <button
            type="button"
            onClick={() => setIsStriking(false)}
            className="mt-1.5 flex h-8 items-center gap-1 px-1 text-[13px] font-medium text-slate-600 hover:text-slate-900"
          >
            <X size={14} />
            {t("smartSurveyor.cablesBeta.cancel")}
          </button>
        </div>
      )}

      {draftName !== null && (
        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={commitName}
          className="mt-1.5 flex h-8 items-center gap-1 rounded-md border border-blue-200 bg-white px-2 text-[13px] font-medium text-blue-700"
        >
          <Check size={14} />
          {t("smartSurveyor.cablesBeta.sourceRenameSave")}
        </button>
      )}
    </li>
  );
};
